import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { ModalGerenciarChecklist } from "./ModalGerenciarChecklist";

interface ChecklistItem {
  id: number;
  checklist_id: number;
  title: string;
  order: number;
  is_checked: boolean;
}

interface DeskChecklist {
  id: number;
  title: string;
  template_id: number | null;
  items: ChecklistItem[];
}

interface ChecklistProps {
  data: any;
  type: string;
  departamentos?: any[];
  departamentoActive?: number;
}

export function Checklist({ data, type, departamentoActive }: ChecklistProps) {
  const { t } = useTranslation();
  const [checklists, setChecklists] = useState<DeskChecklist[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState<number | null>(
    null,
  );
  const [editingChecklistTitle, setEditingChecklistTitle] = useState("");
  const [newItemInputs, setNewItemInputs] = useState<Record<number, string>>(
    {},
  );
  const [showNewItemInput, setShowNewItemInput] = useState<
    Record<number, boolean>
  >({});
  const [editingItem, setEditingItem] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const checklistTitleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchChecklists();
  }, [data?.id, departamentoActive]);

  useEffect(() => {
    if (editingChecklistId !== null && checklistTitleRef.current) {
      checklistTitleRef.current.focus();
    }
  }, [editingChecklistId]);

  async function fetchChecklists() {
    try {
      setLoading(true);
      // Monta o payload conforme o tipo
      const payload: any = {
        type,
        id_departamento: departamentoActive || null,
      };
      if (type === "reunion") {
        payload.reunion_id = data.id;
      } else {
        payload.desk_id = data.id;
      }
      // Remove o campo não usado
      if (type === "reunion") delete payload.desk_id;
      if (type !== "reunion") delete payload.reunion_id;

      const res = await ServiceResumoDesk.filterChecklists(payload);
      if (res?.status === 200) {
        setChecklists(res.data.data || []);
      }
    } catch {
      toast.error(t("checklist.errors.fetch"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateBlank() {
    // Monta o payload conforme o tipo
    const payload: any = {
      type,
      title: t("checklist.default_title"),
      id_departamento: departamentoActive || null,
    };
    if (type === "reunion") {
      payload.reunion_id = data.id;
    } else {
      payload.desk_id = data.id;
    }
    // Remove o campo não usado
    if (type === "reunion") delete payload.desk_id;
    if (type !== "reunion") delete payload.reunion_id;

    const res = await ServiceResumoDesk.createChecklist(payload);
    if (res?.status === 200) {
      setChecklists((prev) => [
        ...prev,
        { ...res.data.data, items: res.data.data.items || [] },
      ]);
    } else {
      toast.error(t("checklist.errors.create"));
    }
  }

  async function handleCreateFromTemplate(
    templateId: number,
    templateTitle: string,
  ) {
    // Monta o payload conforme o tipo
    const payload: any = {
      type,
      title: templateTitle,
      template_id: templateId,
      id_departamento: departamentoActive || null,
    };
    if (type === "reunion") {
      payload.reunion_id = data.id;
    } else {
      payload.desk_id = data.id;
    }
    // Remove o campo não usado
    if (type === "reunion") delete payload.desk_id;
    if (type !== "reunion") delete payload.reunion_id;

    const res = await ServiceResumoDesk.createChecklist(payload);
    if (res?.status === 200) {
      setChecklists((prev) => [...prev, res.data.data]);
      setShowTemplateModal(false);
    } else {
      toast.error(t("checklist.errors.create"));
    }
  }

  async function saveChecklistTitle(checklist: DeskChecklist) {
    const title = editingChecklistTitle.trim();
    setEditingChecklistId(null);
    if (!title || title === checklist.title) return;

    setChecklists((prev) =>
      prev.map((c) => (c.id === checklist.id ? { ...c, title } : c)),
    );
    const res = await ServiceResumoDesk.updateChecklist(checklist.id, {
      title,
    });
    if (res?.status !== 200) {
      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklist.id ? { ...c, title: checklist.title } : c,
        ),
      );
      toast.error(t("checklist.errors.update"));
    }
  }

  async function handleDeleteChecklist(id: number) {
    setChecklists((prev) => prev.filter((c) => c.id !== id));
    const res = await ServiceResumoDesk.deleteChecklist(id);
    if (res?.status !== 200) {
      toast.error(t("checklist.errors.delete"));
      fetchChecklists();
    }
  }

  async function handleAddItem(checklistId: number) {
    const title = (newItemInputs[checklistId] || "").trim();
    if (!title) {
      setShowNewItemInput((prev) => ({ ...prev, [checklistId]: false }));
      return;
    }

    const checklist = checklists.find((c) => c.id === checklistId);
    const nextOrder =
      checklist && checklist.items.length > 0
        ? Math.max(...checklist.items.map((i) => i.order)) + 1
        : 0;

    const res = await ServiceResumoDesk.addChecklistItem({
      checklist_id: checklistId,
      desk_id: data.id,
      type,
      title,
      order: nextOrder,
      id_departamento: departamentoActive || null,
    });

    if (res?.status === 200) {
      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklistId
            ? { ...c, items: [...c.items, res.data.data] }
            : c,
        ),
      );
      setNewItemInputs((prev) => ({ ...prev, [checklistId]: "" }));
      setShowNewItemInput((prev) => ({ ...prev, [checklistId]: false }));
    } else {
      toast.error(t("checklist.errors.create"));
    }
  }

  async function handleToggle(checklistId: number, item: ChecklistItem) {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === item.id ? { ...i, is_checked: !i.is_checked } : i,
              ),
            }
          : c,
      ),
    );
    const res = await ServiceResumoDesk.toggleChecklistItem(item.id);
    if (res?.status !== 200) {
      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklistId
            ? {
                ...c,
                items: c.items.map((i) =>
                  i.id === item.id ? { ...i, is_checked: item.is_checked } : i,
                ),
              }
            : c,
        ),
      );
      toast.error(t("checklist.errors.toggle"));
    }
  }

  async function saveItemEdit(checklistId: number, item: ChecklistItem) {
    const title = (editingItem?.title ?? "").trim();
    setEditingItem(null);
    if (!title || title === item.title) return;

    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === item.id ? { ...i, title } : i,
              ),
            }
          : c,
      ),
    );
    const res = await ServiceResumoDesk.updateChecklistItem(item.id, { title });
    if (res?.status !== 200) {
      setChecklists((prev) =>
        prev.map((c) =>
          c.id === checklistId
            ? {
                ...c,
                items: c.items.map((i) =>
                  i.id === item.id ? { ...i, title: item.title } : i,
                ),
              }
            : c,
        ),
      );
      toast.error(t("checklist.errors.update"));
    }
  }

  async function handleDeleteItem(checklistId: number, itemId: number) {
    setChecklists((prev) =>
      prev.map((c) =>
        c.id === checklistId
          ? { ...c, items: c.items.filter((i) => i.id !== itemId) }
          : c,
      ),
    );
    const res = await ServiceResumoDesk.deleteChecklistItem(itemId);
    if (res?.status !== 200) {
      toast.error(t("checklist.errors.delete"));
      fetchChecklists();
    }
  }

  const totalItems = checklists.reduce((acc, c) => acc + c.items.length, 0);
  const totalChecked = checklists.reduce(
    (acc, c) => acc + c.items.filter((i) => i.is_checked).length,
    0,
  );

  return (
    <div className="space-y-4">
      {/* Header global */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#1E1E1E]/50">
          {totalItems > 0
            ? t("checklist.counter", { done: totalChecked, total: totalItems })
            : t("checklist.no_checklists")}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTemplateModal(true)}
            className="text-xs text-[#1E1E1E]/40 hover:text-azul-400 transition-colors"
          >
            {t("checklist.manage_templates")}
          </button>
          <button
            type="button"
            onClick={handleCreateBlank}
            className="flex items-center gap-1 text-sm text-azul-400 hover:text-azul-600 transition-colors font-medium"
          >
            <span className="text-lg leading-none">+</span>
            {t("checklist.new_checklist")}
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((n) => (
            <div key={n} className="animate-pulse space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-4/5" />
            </div>
          ))}
        </div>
      ) : checklists.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <p className="text-[#1E1E1E]/40 text-sm">
            {t("checklist.empty_state")}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreateBlank}
              className="px-4 py-2 text-sm bg-[#F1F1F1] rounded-2xl text-[#1E1E1E] hover:bg-[#E5E5E5] transition-colors"
            >
              {t("checklist.create_blank")}
            </button>
            <button
              type="button"
              onClick={() => setShowTemplateModal(true)}
              className="px-4 py-2 text-sm bg-azul-400 text-white rounded-2xl hover:opacity-80 transition-opacity"
            >
              {t("checklist.use_template")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {checklists.map((checklist) => {
            const checked = checklist.items.filter((i) => i.is_checked).length;
            const total = checklist.items.length;
            const progress =
              total > 0 ? Math.round((checked / total) * 100) : 0;

            return (
              <div key={checklist.id} className="space-y-2">
                {/* Título do checklist */}
                <div className="flex items-center gap-2 group/checklist">
                  {editingChecklistId === checklist.id ? (
                    <input
                      ref={checklistTitleRef}
                      value={editingChecklistTitle}
                      onChange={(e) => setEditingChecklistTitle(e.target.value)}
                      onBlur={() => saveChecklistTitle(checklist)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveChecklistTitle(checklist);
                        if (e.key === "Escape") setEditingChecklistId(null);
                      }}
                      className="flex-1 text-sm font-medium border-b border-azul-400 outline-none bg-transparent"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingChecklistId(checklist.id);
                        setEditingChecklistTitle(checklist.title);
                      }}
                      className="flex-1 text-left text-sm font-medium text-[#1E1E1E] hover:text-azul-400 transition-colors truncate"
                    >
                      {checklist.title}
                    </button>
                  )}
                  <span className="text-xs text-[#1E1E1E]/40 flex-shrink-0">
                    {checked}/{total}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteChecklist(checklist.id)}
                    className="opacity-0 group-hover/checklist:opacity-100 transition-opacity text-[#1E1E1E]/30 hover:text-red-500 flex-shrink-0"
                    title={t("common:delete")}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0zM4.544 3.5l.85 10.58a1 1 0 0 0 .997.92h6.23a1 1 0 0 0 .997-.92l.85-10.58z" />
                    </svg>
                  </button>
                </div>

                {/* Barra de progresso */}
                {total > 0 && (
                  <div className="h-1.5 w-full bg-[#F1F1F1] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-azul-400 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}

                {/* Itens */}
                <ul className="space-y-0.5">
                  {checklist.items.map((item, index) => (
                    <li
                      key={item.id}
                      className="group flex items-center gap-3 py-1.5 px-2 rounded-xl hover:bg-[#F8F8F8] transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={item.is_checked}
                        onChange={() => handleToggle(checklist.id, item)}
                        className="w-4 h-4 accent-azul-400 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-[#1E1E1E]/30 w-4 flex-shrink-0">
                        {index + 1}
                      </span>
                      {editingItem?.id === item.id ? (
                        <input
                          autoFocus
                          value={editingItem.title}
                          onChange={(e) =>
                            setEditingItem({
                              id: item.id,
                              title: e.target.value,
                            })
                          }
                          onBlur={() => saveItemEdit(checklist.id, item)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              saveItemEdit(checklist.id, item);
                            if (e.key === "Escape") setEditingItem(null);
                          }}
                          className="flex-1 text-sm border-b border-azul-400 outline-none bg-transparent"
                        />
                      ) : (
                        <span
                          onClick={() =>
                            setEditingItem({ id: item.id, title: item.title })
                          }
                          className={`flex-1 text-sm cursor-text ${
                            item.is_checked
                              ? "line-through text-[#1E1E1E]/30"
                              : "text-[#1E1E1E]"
                          }`}
                        >
                          {item.title}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteItem(checklist.id, item.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#1E1E1E]/30 hover:text-red-500 flex-shrink-0"
                        title={t("common:delete")}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                        >
                          <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0zM4.544 3.5l.85 10.58a1 1 0 0 0 .997.92h6.23a1 1 0 0 0 .997-.92l.85-10.58z" />
                        </svg>
                      </button>
                    </li>
                  ))}

                  {showNewItemInput[checklist.id] ? (
                    <li className="flex items-center gap-3 py-1.5 px-2 rounded-xl bg-[#F8F8F8]">
                      <div className="w-4 h-4 border border-[#D2D2D2] rounded flex-shrink-0" />
                      <span className="text-xs text-[#1E1E1E]/30 w-4 flex-shrink-0">
                        {checklist.items.length + 1}
                      </span>
                      <input
                        autoFocus
                        value={newItemInputs[checklist.id] || ""}
                        onChange={(e) =>
                          setNewItemInputs((prev) => ({
                            ...prev,
                            [checklist.id]: e.target.value,
                          }))
                        }
                        onBlur={() => handleAddItem(checklist.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddItem(checklist.id);
                          if (e.key === "Escape") {
                            setShowNewItemInput((prev) => ({
                              ...prev,
                              [checklist.id]: false,
                            }));
                            setNewItemInputs((prev) => ({
                              ...prev,
                              [checklist.id]: "",
                            }));
                          }
                        }}
                        placeholder={t("checklist.item_placeholder")}
                        className="flex-1 text-sm outline-none bg-transparent placeholder:text-[#1E1E1E]/30"
                      />
                    </li>
                  ) : (
                    <li>
                      <button
                        type="button"
                        onClick={() =>
                          setShowNewItemInput((prev) => ({
                            ...prev,
                            [checklist.id]: true,
                          }))
                        }
                        className="flex items-center gap-1 text-xs text-[#1E1E1E]/40 hover:text-azul-400 transition-colors py-1 px-2"
                      >
                        <span className="text-base leading-none">+</span>
                        {t("checklist.add_item")}
                      </button>
                    </li>
                  )}
                </ul>

                <div className="h-px w-full bg-black/5 mt-2" />
              </div>
            );
          })}
        </div>
      )}

      <ModalGerenciarChecklist
        open={showTemplateModal}
        onOpenChange={setShowTemplateModal}
        onApplyTemplate={handleCreateFromTemplate}
      />
    </div>
  );
}
